import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import {
  fetchRecordPages,
  fetchRecordPage,
  createRecordPage as createRecordPageGobodo,
  updateRecordPage as updateRecordPageGobodo,
  deleteRecordPage as deleteRecordPageGobodo,
} from '../gobodo/gobodoRecordPageService';
import { mockObjects } from './objectQueries';
import { createPersistedMockData } from '../mockDataManager';

// ============================================
// TYPES
// ============================================

export type ComponentType =
  | 'field-section'
  | 'related-list'
  | 'activity-timeline'
  | 'path'
  | 'custom-component'
  | 'rich-text'
  | 'report-chart'
  | 'quick-links';

export interface PageComponent {
  id: string;
  type: ComponentType;
  title: string;
  config: Record<string, any>; // Component-specific configuration
  order: number;
}

export interface PageSection {
  id: string;
  name: string;
  columns: 1 | 2;
  components: PageComponent[];
  order: number;
}

export interface PageTab {
  id: string;
  label: string;
  sections: PageSection[];
  isDefault: boolean;
  order: number;
}

export interface RecordPage {
  id: string;
  name: string;
  objectName: string;
  application?: string; // e.g., "Credit Repair", "CyberSweep", or undefined for "All Applications"
  description?: string;
  tabs: PageTab[];
  isActive: boolean;
  createdBy: string;
  createdDate: string;
  lastModified: string;
}

// ============================================
// MOCK DATA
// ============================================

// Build initial data — persisted to localStorage via createPersistedMockData
const _initialRecordPages: RecordPage[] = [];

// Generate record pages for all custom objects
mockObjects.filter(obj => obj.isActive).forEach(obj => {
  // Standard page for all applications
  _initialRecordPages.push({
    id: `page_${obj.apiName}_standard`,
    name: `Standard ${obj.label} Record Page`,
    objectName: obj.apiName,
    application: undefined, // All Applications
    description: `Default record page for ${obj.label} records`,
    tabs: [
      {
        id: 'tab_details',
        label: 'Details',
        isDefault: true,
        order: 0,
        sections: [
          {
            id: 'section_info',
            name: 'Information',
            columns: 2,
            order: 0,
            components: [
              {
                id: 'comp_fields_1',
                type: 'field-section',
                title: 'Basic Information',
                order: 0,
                config: {
                  fields: [], // Will be configured via the page builder
                  columns: 2
                }
              }
            ]
          },
          {
            id: 'section_related',
            name: 'Related',
            columns: 1,
            order: 1,
            components: [
              {
                id: 'comp_related_1',
                type: 'related-list',
                title: 'Related Items',
                order: 0,
                config: {
                  relatedObject: 'Note',
                  fields: ['title', 'created_date'],
                  sortBy: 'created_date',
                  limit: 10
                }
              }
            ]
          }
        ]
      },
      {
        id: 'tab_activity',
        label: 'Activity',
        isDefault: false,
        order: 1,
        sections: [
          {
            id: 'section_timeline',
            name: 'Timeline',
            columns: 1,
            order: 0,
            components: [
              {
                id: 'comp_timeline_1',
                type: 'activity-timeline',
                title: 'Activity Timeline',
                order: 0,
                config: {
                  showEmails: true,
                  showCalls: true,
                  showTasks: true
                }
              }
            ]
          }
        ]
      }
    ],
    isActive: true,
    createdBy: 'System',
    createdDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
    lastModified: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString()
  });

  // Add app-specific pages for Client object
  if (obj.apiName === 'Client__c') {
    _initialRecordPages.push(
      {
        id: `page_${obj.apiName}_credit_repair`,
        name: `${obj.label} Record Page`,
        objectName: obj.apiName,
        application: 'Credit Repair',
        description: `Customized record page for Credit Repair application`,
        tabs: [
          {
            id: 'tab_overview',
            label: 'Overview',
            isDefault: true,
            order: 0,
            sections: [
              {
                id: 'section_key_info',
                name: 'Key Information',
                columns: 2,
                order: 0,
                components: [
                  {
                    id: 'comp_fields_cr',
                    type: 'field-section',
                    title: 'Client Details',
                    order: 0,
                    config: {
                      fields: [], // Will be configured via the page builder
                      columns: 2
                    }
                  }
                ]
              }
            ]
          },
          {
            id: 'tab_disputes',
            label: 'Disputes',
            isDefault: false,
            order: 1,
            sections: [
              {
                id: 'section_disputes',
                name: 'Active Disputes',
                columns: 1,
                order: 0,
                components: [
                  {
                    id: 'comp_related_disputes',
                    type: 'related-list',
                    title: 'Disputes',
                    order: 0,
                    config: {
                      relatedObject: 'Dispute',
                      fields: ['status', 'type', 'created_date'],
                      sortBy: 'created_date'
                    }
                  }
                ]
              }
            ]
          },
          {
            id: 'tab_activity_cr',
            label: 'Activity',
            isDefault: false,
            order: 2,
            sections: [
              {
                id: 'section_timeline_cr',
                name: 'Activity',
                columns: 1,
                order: 0,
                components: [
                  {
                    id: 'comp_timeline_cr',
                    type: 'activity-timeline',
                    title: 'Client Activity',
                    order: 0,
                    config: {}
                  }
                ]
              }
            ]
          }
        ],
        isActive: true,
        createdBy: 'Admin',
        createdDate: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
        lastModified: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        id: `page_${obj.apiName}_cybersweep`,
        name: `${obj.label} Record Page`,
        objectName: obj.apiName,
        application: 'CyberSweep',
        description: `Customized record page for CyberSweep application`,
        tabs: [
          {
            id: 'tab_client_info',
            label: 'Client Info',
            isDefault: true,
            order: 0,
            sections: [
              {
                id: 'section_basic',
                name: 'Basic Information',
                columns: 2,
                order: 0,
                components: [
                  {
                    id: 'comp_fields_cs',
                    type: 'field-section',
                    title: 'Client Information',
                    order: 0,
                    config: {
                      fields: [], // Will be configured via the page builder
                      columns: 2
                    }
                  }
                ]
              }
            ]
          },
          {
            id: 'tab_services',
            label: 'Services',
            isDefault: false,
            order: 1,
            sections: [
              {
                id: 'section_services',
                name: 'Active Services',
                columns: 1,
                order: 0,
                components: [
                  {
                    id: 'comp_related_services',
                    type: 'related-list',
                    title: 'Services',
                    order: 0,
                    config: {
                      relatedObject: 'Service',
                      fields: ['name', 'status', 'start_date'],
                      sortBy: 'start_date'
                    }
                  }
                ]
              }
            ]
          },
          {
            id: 'tab_reports_cs',
            label: 'Reports',
            isDefault: false,
            order: 2,
            sections: [
              {
                id: 'section_reports',
                name: 'Client Reports',
                columns: 1,
                order: 0,
                components: [
                  {
                    id: 'comp_report_chart',
                    type: 'report-chart',
                    title: 'Service Performance',
                    order: 0,
                    config: {
                      reportType: 'bar',
                      dataSource: 'services'
                    }
                  }
                ]
              }
            ]
          }
        ],
        isActive: true,
        createdBy: 'Admin',
        createdDate: new Date(Date.now() - 150 * 24 * 60 * 60 * 1000).toISOString(),
        lastModified: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        id: `page_${obj.apiName}_credit_block`,
        name: `${obj.label} Record Page`,
        objectName: obj.apiName,
        application: 'Credit Block',
        description: `Customized record page for Credit Block application`,
        tabs: [
          {
            id: 'tab_credit_block',
            label: 'Credit Block',
            isDefault: true,
            order: 0,
            sections: [
              {
                id: 'section_cb_info',
                name: 'Credit Block Info',
                columns: 2,
                order: 0,
                components: [
                  {
                    id: 'comp_fields_cb',
                    type: 'field-section' as const,
                    title: 'Client Details',
                    order: 0,
                    config: { fields: [], columns: 2 }
                  }
                ]
              }
            ]
          },
          {
            id: 'tab_bureaus',
            label: 'Credit Bureaus',
            isDefault: false,
            order: 1,
            sections: [
              {
                id: 'section_bureaus',
                name: 'Bureau Credentials',
                columns: 1,
                order: 0,
                components: [
                  {
                    id: 'comp_bureau_list',
                    type: 'related-list' as const,
                    title: 'Credit Bureaus',
                    order: 0,
                    config: { relatedObject: 'CreditBureau', fields: ['bureau', 'username', 'status'] }
                  }
                ]
              }
            ]
          },
          {
            id: 'tab_lock_requests',
            label: 'Lock/Unlock',
            isDefault: false,
            order: 2,
            sections: [
              {
                id: 'section_lock',
                name: 'Lock Requests',
                columns: 1,
                order: 0,
                components: [
                  {
                    id: 'comp_lock_list',
                    type: 'related-list' as const,
                    title: 'Lock/Unlock Requests',
                    order: 0,
                    config: { relatedObject: 'UnlockRequest', fields: ['bureau', 'status', 'created_date'] }
                  }
                ]
              }
            ]
          }
        ],
        isActive: true,
        createdBy: 'Admin',
        createdDate: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString(),
        lastModified: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString()
      }
    );
  }
});

/**
 * Persisted record pages — survives page refresh via localStorage.
 * Used by Object Manager (RecordPagesHome) and read by fetchPageConfiguration
 * in configurationQueries.ts to drive CyberSweep / Credit Block tab structure.
 */
export const persistedOMRecordPages = createPersistedMockData<RecordPage>(
  'crm_om_record_pages',
  _initialRecordPages
);

// ============================================
// QUERY KEYS
// ============================================

export const recordPageKeys = {
  all: ['record-pages'] as const,
  lists: () => [...recordPageKeys.all, 'list'] as const,
  list: (objectName: string) => [...recordPageKeys.lists(), objectName] as const,
  details: () => [...recordPageKeys.all, 'detail'] as const,
  detail: (id: string) => [...recordPageKeys.details(), id] as const,
};

// ============================================
// HELPERS
// ============================================

/**
 * Normalize application name to the pageId prefix used by configurationQueries.
 * e.g. "CyberSweep" → "cybersweep", "Credit Block" → "credit_block"
 */
function normalizeAppName(app?: string): string | null {
  if (!app) return null;
  return app.toLowerCase().replace(/\s+/g, '_');
}

/** Strip Gobodo env suffix for object name comparison (e.g. Client__stg3_c → client) */
function normalizeObjectName(name: string): string {
  return name.replace(/(__stg3_c|__stg_c|__c)$/i, '').toLowerCase();
}

// ============================================
// HOOKS
// ============================================

/**
 * Get all record pages for an object.
 * When USE_GOBODO_API is true: tries Gobodo first, falls back to localStorage
 * if Gobodo returns no records (e.g. RecordPage objects not yet created in this env).
 * Object name matching is normalized to handle env suffixes (Client__stg3_c vs Client__c).
 */
export function useRecordPages(objectName: string) {
  return useQuery({
    queryKey: recordPageKeys.list(objectName),
    queryFn: async () => {
      const normalizedInput = normalizeObjectName(objectName);
      const getLocalPages = () =>
        persistedOMRecordPages.get().filter(
          page => normalizeObjectName(page.objectName) === normalizedInput
        );

      if (USE_GOBODO_API) {
        const gobodoPages = await fetchRecordPages(objectName);
        // Fall back to localStorage when Gobodo has no RecordPage records yet
        return gobodoPages.length > 0 ? gobodoPages : getLocalPages();
      }

      await new Promise(resolve => setTimeout(resolve, 400));
      return getLocalPages();
    },
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Get single record page
 */
export function useRecordPage(pageId: string) {
  return useQuery({
    queryKey: recordPageKeys.detail(pageId),
    queryFn: async () => {
      if (USE_GOBODO_API) {
        return fetchRecordPage(pageId);
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const page = persistedOMRecordPages.get().find(p => p.id === pageId);
      if (!page) throw new Error('Record page not found');
      return page;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!pageId,
  });
}

/**
 * Create new record page
 */
export function useCreateRecordPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pageData: Omit<RecordPage, 'id' | 'createdBy' | 'createdDate' | 'lastModified'>) => {
      if (USE_GOBODO_API) {
        return createRecordPageGobodo(pageData);
      }
      await new Promise(resolve => setTimeout(resolve, 600));
      const newPage: RecordPage = {
        ...pageData,
        id: `page_${Date.now()}`,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
      };
      return persistedOMRecordPages.add(newPage);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: recordPageKeys.list(data.objectName) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
      // Invalidate app page configs so CyberSweep/Credit Block re-render with new tabs
      const appKey = normalizeAppName(data.application);
      if (appKey) {
        queryClient.invalidateQueries({ queryKey: ['configuration', 'pages', `${appKey}_client_detail`] });
      }
    },
  });
}

/**
 * Update record page
 */
export function useUpdateRecordPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pageId, updates }: { pageId: string; updates: Partial<RecordPage> }) => {
      if (USE_GOBODO_API) {
        return updateRecordPageGobodo(pageId, updates);
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      const updated = persistedOMRecordPages.update(pageId, {
        ...updates,
        lastModified: new Date().toISOString(),
      });
      if (!updated) throw new Error('Record page not found');
      return updated;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: recordPageKeys.list(data.objectName) });
      queryClient.invalidateQueries({ queryKey: recordPageKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
      // Invalidate app page configs so CyberSweep/Credit Block re-render with updated tabs
      const appKey = normalizeAppName(data.application);
      if (appKey) {
        queryClient.invalidateQueries({ queryKey: ['configuration', 'pages', `${appKey}_client_detail`] });
      }
    },
  });
}

/**
 * Delete record page
 */
export function useDeleteRecordPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pageId, objectName }: { pageId: string; objectName: string }) => {
      if (USE_GOBODO_API) {
        await deleteRecordPageGobodo(pageId);
        return { pageId, objectName };
      }
      await new Promise(resolve => setTimeout(resolve, 400));
      const removed = persistedOMRecordPages.remove(pageId);
      if (!removed) throw new Error('Record page not found');
      return { pageId, objectName };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: recordPageKeys.list(data.objectName) });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get component type label
 */
export function getComponentTypeLabel(type: ComponentType): string {
  const labels: Record<ComponentType, string> = {
    'field-section': 'Field Section',
    'related-list': 'Related List',
    'activity-timeline': 'Activity Timeline',
    'path': 'Path',
    'custom-component': 'Custom Component',
    'rich-text': 'Rich Text',
    'report-chart': 'Report Chart',
    'quick-links': 'Quick Links'
  };
  return labels[type];
}

/**
 * Get component type icon
 */
export function getComponentTypeIcon(type: ComponentType): string {
  const icons: Record<ComponentType, string> = {
    'field-section': 'layout',
    'related-list': 'list',
    'activity-timeline': 'clock',
    'path': 'gitBranch',
    'custom-component': 'box',
    'rich-text': 'fileText',
    'report-chart': 'barChart',
    'quick-links': 'link'
  };
  return icons[type];
}

/**
 * Count total components in page
 */
export function countComponents(page: RecordPage): number {
  return page.tabs.reduce((total, tab) => {
    return total + tab.sections.reduce((sectionTotal, section) => {
      return sectionTotal + section.components.length;
    }, 0);
  }, 0);
}
